import React from "react";
import { FiDownload, FiExternalLink } from "react-icons/fi";
import { Button } from "newui/components/Button";
import { useNavigationPaths } from "newui/features/Auth/hooks/useNavigationPaths";
import { useRfxIndividualResponseExport } from "newui/features/RfxReports/hooks/useRfxResponseExport";

interface ResponseAccessActionsProps {
	rfxId: number;
	inviteId: number;
	/** Disable when the viewer lacks `viewResponseReport` access (e.g. 403). */
	disabled?: boolean;
}

/**
 * Per-supplier response access: open the supplier's evaluation response in a new
 * tab and download its PDF report. Both reuse existing mechanisms — the
 * `viewRfxSubmission` client path and the RfxReports individual-response export
 * (which opens the server `download_url`); no client-side URL construction.
 */
export const ResponseAccessActions: React.FC<ResponseAccessActionsProps> = ({
	rfxId,
	inviteId,
	disabled = false,
}) => {
	const { clientPaths } = useNavigationPaths();
	const responseExport = useRfxIndividualResponseExport();

	const handleOpenResponse = () => {
		window.open(
			clientPaths.viewRfxSubmission(rfxId, inviteId),
			"_blank",
			"noopener,noreferrer"
		);
	};

	const handleDownloadPdf = () => {
		responseExport.mutate({ rfxId, inviteId });
	};

	return (
		<div className="inline-flex gap-2" data-testid={`response-actions-${inviteId}`}>
			<Button
				btntype="secondary"
				type="button"
				size="xs"
				disabled={disabled}
				disabledStyle={disabled}
				onClick={handleOpenResponse}
				data-testid={`open-response-${inviteId}`}
			>
				<span className="flex items-center gap-1">
					<FiExternalLink /> Open Response
				</span>
			</Button>
			<Button
				btntype="secondary"
				type="button"
				size="xs"
				disabled={disabled}
				disabledStyle={disabled}
				isLoading={responseExport.isPending}
				onClick={handleDownloadPdf}
				data-testid={`download-response-${inviteId}`}
			>
				<span className="flex items-center gap-1">
					<FiDownload /> Download Response PDF
				</span>
			</Button>
		</div>
	);
};
